import { z } from 'zod';
export const MAX_ENGAGEMENT_CHAT_ATTACHMENTS = 10;
export const ENGAGEMENT_CHAT_ATTACHMENT_MAX_SIZE_MB = 20;
export const ENGAGEMENT_CHAT_VIDEO_ATTACHMENT_MAX_SIZE_MB = 30;
const BYTES_PER_MB = 1024 * 1024;
const ALLOWED_DOCUMENT_MIME_TYPES = new Set([
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
]);
const DISALLOWED_IMAGE_MIME_TYPES = new Set(['image/svg+xml']);
const EXTENSION_TO_MIME_TYPE = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    bmp: 'image/bmp',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    webm: 'video/webm',
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    txt: 'text/plain',
};
export const ENGAGEMENT_CHAT_ATTACHMENT_ACCEPT = [
    'image/*',
    'video/*',
    ...ALLOWED_DOCUMENT_MIME_TYPES,
    ...Object.entries(EXTENSION_TO_MIME_TYPE)
        .filter(([, mimeType]) => ALLOWED_DOCUMENT_MIME_TYPES.has(mimeType))
        .map(([extension]) => `.${extension}`),
].join(',');
export const engagementChatAttachmentCandidateSchema = z.object({
    name: z.string(),
    type: z.string(),
    size: z.number(),
});
function extensionFromFileName(fileName) {
    return fileName.split('.').pop()?.toLowerCase();
}
function mimeTypeFromExtension(fileName) {
    const extension = extensionFromFileName(fileName);
    if (extension === undefined) {
        return '';
    }
    return EXTENSION_TO_MIME_TYPE[extension] ?? '';
}
export function resolveEngagementChatAttachmentMimeType(file) {
    const normalizedType = file.type.trim().toLowerCase();
    if (normalizedType.length > 0 && normalizedType !== 'application/octet-stream') {
        return normalizedType;
    }
    return mimeTypeFromExtension(file.name);
}
export function isEngagementChatAttachmentMimeTypeAllowed(mimeType) {
    const normalizedMimeType = mimeType.trim().toLowerCase();
    if (normalizedMimeType.length === 0 || DISALLOWED_IMAGE_MIME_TYPES.has(normalizedMimeType)) {
        return false;
    }
    if (normalizedMimeType.startsWith('image/') || normalizedMimeType.startsWith('video/')) {
        return true;
    }
    return ALLOWED_DOCUMENT_MIME_TYPES.has(normalizedMimeType);
}
export function getEngagementChatAttachmentMaxSizeMb(mimeType) {
    return mimeType.trim().toLowerCase().startsWith('video/')
        ? ENGAGEMENT_CHAT_VIDEO_ATTACHMENT_MAX_SIZE_MB
        : ENGAGEMENT_CHAT_ATTACHMENT_MAX_SIZE_MB;
}
export function getEngagementChatAttachmentMaxBytes(mimeType) {
    return getEngagementChatAttachmentMaxSizeMb(mimeType) * BYTES_PER_MB;
}
export function validateEngagementChatAttachmentFile(file) {
    const mimeType = resolveEngagementChatAttachmentMimeType(file);
    if (!isEngagementChatAttachmentMimeTypeAllowed(mimeType)) {
        return { reason: 'unsupported_type', fileName: file.name };
    }
    const maxSizeMb = getEngagementChatAttachmentMaxSizeMb(mimeType);
    if (file.size > maxSizeMb * BYTES_PER_MB) {
        return { reason: 'too_large', fileName: file.name, maxSizeMb };
    }
    return null;
}
export const engagementChatAttachmentUploadSchema = engagementChatAttachmentCandidateSchema.superRefine((file, ctx) => {
    const failure = validateEngagementChatAttachmentFile(file);
    if (failure === null) {
        return;
    }
    ctx.addIssue({
        code: 'custom',
        message: failure.reason === 'too_large'
            ? `Attachment exceeds ${failure.maxSizeMb} MB`
            : 'Attachment type is not supported',
        path: [failure.reason === 'too_large' ? 'size' : 'type'],
        params: { failure },
    });
});
export function engagementChatAttachmentFailureOf(error) {
    for (const issue of error.issues) {
        const failure = issue.params?.failure;
        if (failure !== undefined) {
            return failure;
        }
    }
    return undefined;
}
export function validateEngagementChatAttachmentFiles(files, { pendingCount }) {
    const accepted = [];
    const rejections = [];
    let slotsLeft = MAX_ENGAGEMENT_CHAT_ATTACHMENTS - pendingCount;
    for (const file of files) {
        if (slotsLeft <= 0) {
            rejections.push({
                file,
                failure: { reason: 'too_many', maxCount: MAX_ENGAGEMENT_CHAT_ATTACHMENTS },
            });
            continue;
        }
        const failure = validateEngagementChatAttachmentFile(file);
        if (failure !== null) {
            rejections.push({ file, failure });
            continue;
        }
        accepted.push(file);
        slotsLeft -= 1;
    }
    return { accepted, rejections };
}
